/**
 * /ai-coding/mcp 配下の Tested Guide が共有する構造化データ。
 *
 * 4 ページ (server / figma / playwright / notion) の本文はここが正本で、
 * ページ側はこれを描画するだけにする。クライアント別の差分・権限・
 * 失敗・確認日はすべてフィールドとして持つので、バージョンが変わった
 * ときは数値を書き換えて再テストすればよい。
 *
 * 状態の約束:
 * - "verified" はこの作業機で実際に再現して確認したものだけに付ける
 * - ベンダーが接続可と案内していても、こちらで確かめていなければ
 *   "unverified" (未観測) にする
 * - 失敗は起きた事実だけを書く。推測で失敗をでっち上げない
 */
import type { AcquisitionEntity } from "@/lib/analytics/acquisition-funnel";

/** マトリクスの列になるクライアント。funnel の entity 値に揃える */
export type McpClientId = Extract<
  AcquisitionEntity,
  "claude_code" | "codex" | "cursor" | "vscode"
>;

/**
 * 実機の AI クライアントではなく、initialize / tools/list / tools/call を
 * 仕様どおりに投げるだけの検証用スクリプト。「クライアント越しでは
 * 試していないが、サーバー応答そのものは確認した」を列に分けて書くための
 * 識別子。イベントの entity には使わない (AcquisitionEntity 外)。
 */
export const MCP_PROBE_CLIENT_ID = "probe" as const;
export type McpMatrixClientId = McpClientId | typeof MCP_PROBE_CLIENT_ID;

export type McpSupportStatus =
  | "verified" // この環境で確認済み
  | "partial" // 一部だけ確認 (例: 設定形式は見たが動作確認は未)
  | "unverified" // 未観測
  | "failed"; // 失敗を確認済み (再現した)

export interface McpClientColumn {
  id: McpMatrixClientId;
  /** 列見出し */
  label: string;
  /** この環境に入っていた版。「—」は未導入 */
  testedVersion?: string;
}

export interface McpMatrixRow {
  /** 行の検証項目 */
  feature: string;
  /** クライアントごとの状態。キーが無い列は「—」(対象外) */
  cells: Partial<Record<McpMatrixClientId, McpSupportStatus>>;
  /** 状態だけでは伝わらない注記 (1 文) */
  note?: string;
}

export interface McpSetupCode {
  filename: string;
  language: string;
  code: string;
}

export interface McpSetupStep {
  /** setup_step_complete の step プロパティに乗る id */
  id: string;
  title: string;
  detail: string;
  code?: McpSetupCode;
}

export interface McpClientSetup {
  client: McpClientId;
  /** 設定ファイルや登録方法の在処 ("~/.claude.json" など) */
  configPath: string;
  /** このクライアントについてこの環境で見たこと (1 文) */
  note?: string;
  steps: McpSetupStep[];
}

export interface McpAuthScope {
  label: string;
  value: string;
}

export interface McpKnownFailure {
  symptom: string;
  /** 観測内容。再現できていないものは「未観測」と書く */
  observation: string;
  workaround?: string;
}

export interface McpConnectionCheck {
  /** 「接続できた」と判断する材料の説明 */
  detail: string;
  code?: McpSetupCode;
}

export type McpGuideSlug = "server" | "figma" | "playwright" | "notion";

export interface McpGuide {
  slug: McpGuideSlug;
  name: string;
  /** 検索で拾いたい語を先頭に置いたページタイトル (meta) */
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  /** H1 の下に出す副題 */
  tagline: string;
  /** 導入段落 */
  summary: string;
  /** 対象の配布物・エンドポイント */
  serverTarget: string;
  /** 確かめた版。"hosted" はホスト型エンドポイント */
  testedVersion: string;
  /** YYYY-MM-DD。版が変わったら再テストしてここを更新する */
  lastTestedAt: string;
  /** 確かめた環境を一行で */
  testEnvironment: string;
  /** この環境でサーバー単体として確認したこと (クライアント非依存) */
  verifiedObservations: string[];
  clients: McpClientColumn[];
  matrix: McpMatrixRow[];
  setups: McpClientSetup[];
  connectionCheck: McpConnectionCheck;
  authScopes: McpAuthScope[];
  knownFailures: McpKnownFailure[];
  /** 隣のガイド。最大 2 件 */
  siblings: McpGuideSlug[];
  /** LastTested 札のステータス文 */
  lastTestedStatus: string;
  lastTestedNote: string;
}

/**
 * マトリクスの列。この作業機 (2026-09-17 時点) に入っているのは
 * Claude Code 2.1.81 と Codex CLI 0.144.3 のみ。Cursor と VS Code は
 * 未導入なので、それらの列は原則 未観測 で埋める。
 */
export const MCP_CLIENT_COLUMNS: readonly McpClientColumn[] = [
  { id: "claude_code", label: "Claude Code", testedVersion: "2.1.81" },
  { id: "codex", label: "Codex CLI", testedVersion: "0.144.3" },
  { id: "cursor", label: "Cursor" },
  { id: "vscode", label: "VS Code" },
  {
    id: MCP_PROBE_CLIENT_ID,
    label: "検証クライアント",
    testedVersion: "Node 26 / SDK 1.30.0",
  },
];

export const MCP_GUIDES: readonly McpGuide[] = [
  {
    slug: "server",
    name: "MCPサーバーの作り方",
    metaTitle: "MCPサーバーの作り方｜SDKで建ててClaude Code・Codexに接続する",
    metaDescription:
      "Model Context Protocol のサーバーを公式SDKで建て、AIコーディングツールに登録するところまでの手順です。実際に接続確認した結果つき。",
    keywords: ["MCPサーバー", "MCP 作り方", "Model Context Protocol", "MCP 接続"],
    tagline: "サーバー側を自分で建てて、クライアントに登録するまでの最短経路。",
    summary:
      "MCPサーバーは「ツール一覧を返し、呼ばれたら結果を返す」だけのプロセスです。" +
      "公式の TypeScript SDK で最小構成を作り、stdio 経由でクライアントに登録します。" +
      "このページのコードは検証環境で実際に起動・呼び出しまで確認しました。",
    serverTarget: "@modelcontextprotocol/sdk",
    testedVersion: "1.30.0",
    lastTestedAt: "2026-09-17",
    testEnvironment: "macOS arm64 / Node 26 / Claude Code 2.1.81 / Codex CLI 0.144.3",
    verifiedObservations: [
      "SDK で建てた最小サーバー (echo ツール 1 本) に検証クライアントから接続し、initialize → tools/list → tools/call が往復した",
      "Claude Code の設定ファイル ~/.claude.json が mcpServers キーでサーバー定義を持つことを実ファイルで確認した",
      "Codex CLI の codex mcp add が ~/.codex/config.toml に [mcp_servers.<名前>] テーブルを書き、codex mcp list で enabled と出ることを確認した",
    ],
    clients: [...MCP_CLIENT_COLUMNS],
    matrix: [
      {
        feature: "設定ファイルへの登録",
        cells: {
          claude_code: "partial",
          codex: "verified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "Claude Code は ~/.claude.json の mcpServers 形式を確認。Codex は codex mcp add で config.toml に書き込まれることを確認。",
      },
      {
        feature: "stdio での initialize / tools 応答",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "SDK 製クライアントでの往復は確認済み。各 AI クライアント越しの呼び出しは未観測。",
      },
      {
        feature: "リモート (HTTP) 接続",
        cells: {
          claude_code: "partial",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "Claude Code の mcpServers に type: \"http\" + url の実在定義を確認済み。実際のリモート接続は未観測。",
      },
    ],
    setups: [
      {
        client: "claude_code",
        configPath: "~/.claude.json (mcpServers)",
        note: "この環境の実ファイルに mcpServers 定義があり、形式を確認済み。",
        steps: [
          {
            id: "server-write",
            title: "サーバーを作る",
            detail:
              "公式 SDK の McpServer にツールを登録し、StdioServerTransport で待ち受ける最小構成。",
            code: {
              filename: "server.ts",
              language: "typescript",
              code: `import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { z } from "zod";

const server = new McpServer({ name: "my-mcp", version: "0.1.0" });

server.registerTool(
  "echo",
  { description: "渡した文字をそのまま返す", inputSchema: { text: z.string() } },
  async ({ text }) => ({ content: [{ type: "text", text }] }),
);

await server.connect(new StdioServerTransport());`,
            },
          },
          {
            id: "server-register",
            title: "Claude Code に登録する",
            detail:
              "claude mcp add で登録するか、~/.claude.json の mcpServers に直接書く。",
            code: {
              filename: "ターミナル",
              language: "bash",
              code: `claude mcp add my-mcp -- npx tsx /path/to/server.ts`,
            },
          },
        ],
      },
      {
        client: "codex",
        configPath: "~/.codex/config.toml (mcp_servers)",
        note: "codex mcp add が config.toml に書き、list で enabled になることを確認済み。",
        steps: [
          {
            id: "server-write",
            title: "サーバーを作る",
            detail: "Claude Code 用と同じコードで動く。SDK が違いを吸収する。",
          },
          {
            id: "server-register",
            title: "Codex に登録する",
            detail:
              "codex mcp add が ~/.codex/config.toml に [mcp_servers.<名前>] を書くことを確認済み。",
            code: {
              filename: "ターミナル",
              language: "bash",
              code: `codex mcp add my-mcp -- npx tsx /path/to/server.ts
# → config.toml に [mcp_servers.my-mcp] command="npx" args=[...] が書かれる`,
            },
          },
        ],
      },
      {
        client: "cursor",
        configPath: ".cursor/mcp.json または設定 UI",
        note: "この環境には未導入のため、手順は公開情報ベース・動作は未観測。",
        steps: [
          {
            id: "server-register",
            title: "mcp.json に登録する",
            detail:
              "プロジェクトの .cursor/mcp.json か設定画面に command/args を書く形式が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "mcpServers": {
    "my-mcp": { "command": "npx", "args": ["tsx", "/path/to/server.ts"] }
  }
}`,
            },
          },
        ],
      },
      {
        client: "vscode",
        configPath: ".vscode/mcp.json",
        note: "この環境には未導入のため、手順は公開情報ベース・動作は未観測。",
        steps: [
          {
            id: "server-register",
            title: "mcp.json に登録する",
            detail:
              "ワークスペースの .vscode/mcp.json に servers を書く形式が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "servers": {
    "my-mcp": { "type": "stdio", "command": "npx", "args": ["tsx", "/path/to/server.ts"] }
  }
}`,
            },
          },
        ],
      },
    ],
    connectionCheck: {
      detail:
        "クライアントの MCP 一覧に自分のサーバーが出て、ツール名 (ここでは echo) が見えれば接続できている。Codex では codex mcp list の Status が enabled になることを確認済み。",
      code: {
        filename: "ターミナル",
        language: "bash",
        code: `codex mcp list          # Status 列が enabled なら登録成功 (確認済み)
claude mcp list        # Claude Code 側の一覧 (ログイン済み環境で実行)`,
      },
    },
    authScopes: [
      {
        label: "stdio 接続",
        value: "認証なし。クライアントが子プロセスとして起動するため、設定ファイルの command/args を書けることが実質の権限。",
      },
      {
        label: "環境変数",
        value: "トークン等は設定の env に書くと子プロセスへ渡る。設定ファイルは平文なので、入れる値の管理に注意。",
      },
      {
        label: "リモート (HTTP) 接続",
        value: "ホスト側の OAuth 保護リソースに従う。Figma/Notion のガイドに実測した challenge 例あり。",
      },
    ],
    knownFailures: [
      {
        symptom: "claude mcp add が応答しない",
        observation:
          "ログイン・初期設定が済んでいない HOME で実行したところ、40 秒待っても終了せず .claude.json も書かれなかった。対話ログイン済みの環境で使うか、設定ファイルを直接書く。",
        workaround: "~/.claude.json の mcpServers に JSON で直接登録する (形式は確認済み)。",
      },
      {
        symptom: "npx 経由サーバーの初回 handshake がタイムアウトする",
        observation:
          "@notionhq/notion-mcp-server を初めて npx で起動した際、パッケージ取得中は initialize に応答せず 25 秒でタイムアウトした。2 回目 (キャッシュ済み) は即応答。",
        workaround: "先に npx -y <パッケージ> を一度素で実行してキャッシュを温める。",
      },
    ],
    siblings: ["playwright", "notion"],
    lastTestedStatus: "SDK 往復・登録経路は確認済み",
    lastTestedNote:
      "サーバーの往復は検証クライアントで確認。各 AI クライアントの画面越しの呼び出しは未観測。バージョンが変わったら testedVersion と lastTestedAt を更新して再テストする。",
  },
  {
    slug: "figma",
    name: "Figma MCP",
    metaTitle: "Figma MCP の設定方法｜対応クライアントと認証まわり",
    metaDescription:
      "Figma の公式 MCP サーバー (リモート/デスクトップ) を AI コーディングツールから使うための接続ガイド。認証の実測結果つき。",
    keywords: ["Figma MCP", "Figma MCP 設定", "Figma MCP Claude Code"],
    tagline: "Figma のデザイン情報をエージェントに渡す公式サーバー。",
    summary:
      "Figma はホスト型のリモートサーバー (mcp.figma.com) と、Figma デスクトップアプリが" +
      "ローカルに立てるサーバーの 2 系統を公式に出しています。リモート側は OAuth で守られており、" +
      "接続できるクライアントは Figma の MCP カタログ掲載分に限られます。",
    serverTarget: "https://mcp.figma.com/mcp",
    testedVersion: "hosted (2026-09-17 時点)",
    lastTestedAt: "2026-09-17",
    testEnvironment: "macOS arm64 / curl によるエンドポイント確認",
    verifiedObservations: [
      "リモート endpoint に未認証で initialize を投げると 401 と WWW-Authenticate: Bearer (scope mcp:connect, 認可サーバー api.figma.com) が返ることを確認した",
      "oauth-protected-resource のメタデータが公開されており、Bearer は Authorization ヘッダーで渡す方式と確認した",
      "Figma MCP カタログに Claude Code / Codex / Cursor / VS Code が掲載されていることを確認した (いずれもリモート+ローカル・読み書き両対応の表記)",
    ],
    clients: [...MCP_CLIENT_COLUMNS],
    matrix: [
      {
        feature: "Figma MCP カタログ掲載 (公式案内)",
        cells: {
          claude_code: "verified",
          codex: "verified",
          cursor: "verified",
          vscode: "verified",
          probe: "unverified",
        },
        note: "カタログ掲載の確認は済み。カタログ外のクライアントはリモート接続自体が受け付けられない建付け。",
      },
      {
        feature: "OAuth 認可チャレンジへの応答",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "未認証アクセスで 401 + Bearer challenge を確認。クライアント側の認可フロー完了は未観測。",
      },
      {
        feature: "フレームの読み取り・書き込み",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "実データ操作には Figma アカウントでの OAuth 認可が必要。この環境では未観測。",
      },
      {
        feature: "デスクトップ (ローカル) サーバー",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "Figma デスクトップアプリがこの環境に無く未観測。",
      },
    ],
    setups: [
      {
        client: "claude_code",
        configPath: "~/.claude.json (mcpServers)",
        note: "http 型の定義形式は実ファイルで確認済み。認可フロー自体は未観測。",
        steps: [
          {
            id: "figma-register",
            title: "リモートサーバーを登録する",
            detail:
              "mcp.figma.com/mcp を http 型で登録する。実ファイル上の http 型定義の形は確認済み。",
            code: {
              filename: "~/.claude.json",
              language: "json",
              code: `"mcpServers": {
  "figma": { "type": "http", "url": "https://mcp.figma.com/mcp" }
}`,
            },
          },
          {
            id: "figma-auth",
            title: "Figma アカウントで認可する",
            detail:
              "初回接続時に Figma の OAuth 画面へ進み、scope mcp:connect の認可を求められる (challenge は確認済み、完了後の動作は未観測)。",
          },
        ],
      },
      {
        client: "codex",
        configPath: "~/.codex/config.toml (mcp_servers)",
        note: "stdio サーバーの登録は確認済み。リモート + OAuth の codex mcp login は未観測。",
        steps: [
          {
            id: "figma-register",
            title: "リモートサーバーを登録する",
            detail:
              "Codex は mcp_servers に URL 型の定義と codex mcp login による OAuth を持つ。コマンドの存在は codex mcp --help で確認済み。",
            code: {
              filename: "config.toml",
              language: "text",
              code: `[mcp_servers.figma]
url = "https://mcp.figma.com/mcp"   # 書式は codex mcp add --help / 公式案内を確認して合わせる`,
            },
          },
          {
            id: "figma-auth",
            title: "codex mcp login で認可する",
            detail: "codex mcp login figma が OAuth フローを開始する (サブコマンドの存在は確認済み、完了は未観測)。",
            code: {
              filename: "ターミナル",
              language: "bash",
              code: `codex mcp login figma`,
            },
          },
        ],
      },
      {
        client: "cursor",
        configPath: "Cursor の MCP 設定",
        note: "未導入。カタログ掲載は確認済みだが手順・動作は未観測。",
        steps: [
          {
            id: "figma-register",
            title: "カタログの手順で登録する",
            detail:
              "Figma MCP カタログに Cursor 用の導線がある。リモート接続 + OAuth が前提。",
          },
        ],
      },
      {
        client: "vscode",
        configPath: ".vscode/mcp.json",
        note: "未導入。カタログ掲載は確認済みだが手順・動作は未観測。",
        steps: [
          {
            id: "figma-register",
            title: "カタログの手順で登録する",
            detail:
              "Figma MCP カタログに VS Code 用の導線がある。リモート接続 + OAuth が前提。",
          },
        ],
      },
    ],
    connectionCheck: {
      detail:
        "未認証で https://mcp.figma.com/mcp へ initialize を投げ、401 と Bearer challenge (scope mcp:connect) が返ればエンドポイントは生きている。認可後の確認はクライアント側の接続表示を見る (未観測)。",
      code: {
        filename: "ターミナル",
        language: "bash",
        code: `curl -i -X POST https://mcp.figma.com/mcp \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{"protocolVersion":"2025-06-18","capabilities":{},"clientInfo":{"name":"probe","version":"0.0.1"}}}'
# → 401 + WWW-Authenticate: Bearer ... scope="mcp:connect" (確認済み)`,
      },
    },
    authScopes: [
      {
        label: "認証方式",
        value: "OAuth Bearer。scope は mcp:connect、認可サーバーは api.figma.com (メタデータで確認済み)。",
      },
      {
        label: "接続可能クライアント",
        value: "Figma MCP カタログ掲載のクライアントのみ。カタログ外はリモート接続を受け付けない建付け (公式案内)。",
      },
      {
        label: "ローカルサーバー",
        value: "Figma デスクトップアプリが起動する 127.0.0.1 上のサーバー。組織・Enterprise 向け案内あり。この環境では未観測。",
      },
    ],
    knownFailures: [
      {
        symptom: "未認証の initialize が 401 になる",
        observation:
          "WWW-Authenticate: Bearer resource_metadata=... scope=\"mcp:connect\" が返る。認可なしでは一切の MCP 応答が得られない (確認済み)。",
        workaround: "クライアント側の OAuth フローを完了させる。challenge 自体が返れば endpoint 疎通は成功。",
      },
      {
        symptom: "デスクトップサーバーに接続できない",
        observation: "未観測 (Figma デスクトップ未導入の環境のため)。",
      },
    ],
    siblings: ["playwright", "notion"],
    lastTestedStatus: "認証チャレンジまで確認・実操作は未観測",
    lastTestedNote:
      "リモート endpoint の 401 challenge と OAuth メタデータは実測済み。Figma アカウントが必要なため、認可後のツール呼び出しとデスクトップサーバーは未観測と明記している。",
  },
  {
    slug: "playwright",
    name: "Playwright MCP",
    metaTitle: "Playwright MCP の設定方法｜ブラウザ操作をAIツールに繋ぐ",
    metaDescription:
      "Playwright MCP を Claude Code や Codex に登録してブラウザ操作を任せる手順。ツール一覧の取得と実際の画面遷移まで確認した結果つき。",
    keywords: ["Playwright MCP", "Playwright MCP 設定", "MCP ブラウザ"],
    tagline: "ブラウザ操作をツールとして公開する MCP サーバー。",
    summary:
      "Playwright MCP は npx 一発で立つ stdio サーバーで、ナビゲーション・クリック・" +
      "スクリーンショットなどのツールをエージェントへ公開します。この環境では起動から" +
      "ツール一覧の取得、data: URL への実際の画面遷移まで確認しました。",
    serverTarget: "@playwright/mcp",
    testedVersion: "0.0.81 (server は Playwright 1.64.0-alpha-2026-09-14 と応答)",
    lastTestedAt: "2026-09-17",
    testEnvironment:
      "macOS arm64 / Node 26 / chromium-1200 (~/Library/Caches/ms-playwright)",
    verifiedObservations: [
      "initialize に protocolVersion 2025-06-18 で応答し、serverInfo は {\"name\":\"Playwright\",\"version\":\"1.64.0-alpha-2026-09-14\"} だった",
      "tools/list で 26 個のツール (browser_navigate, browser_click, browser_take_screenshot 等) が返った",
      "browser_navigate に data: URL を渡すと、インストール済みの headless Chromium が実際に遷移しスナップショットを返した",
      "codex mcp add playwright -- npx -y @playwright/mcp@0.0.81 が config.toml に書き込み、codex mcp list で enabled と出た",
    ],
    clients: [...MCP_CLIENT_COLUMNS],
    matrix: [
      {
        feature: "クライアントへの登録",
        cells: {
          claude_code: "partial",
          codex: "verified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "Codex は mcp add → list enabled まで確認。Claude Code は設定ファイル形式のみ確認 (CLI 登録は未観測)。",
      },
      {
        feature: "initialize / tools/list",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "検証クライアントでは 26 ツールを確認。AI クライアント越しは未観測。",
      },
      {
        feature: "browser_navigate による実遷移",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "data: URL への遷移とスナップショット返却を確認。AI クライアント経由は未観測。",
      },
    ],
    setups: [
      {
        client: "claude_code",
        configPath: "~/.claude.json (mcpServers)",
        note: "設定形式は確認済み。CLI での登録完了はこの環境では未観測。",
        steps: [
          {
            id: "pw-register",
            title: "サーバーを登録する",
            detail: "claude mcp add または ~/.claude.json の mcpServers に追記する。",
            code: {
              filename: "ターミナル",
              language: "bash",
              code: `claude mcp add playwright -- npx -y @playwright/mcp@0.0.81`,
            },
          },
          {
            id: "pw-json",
            title: "設定ファイルで確認する",
            detail:
              "mcpServers 配下に command/args の定義があれば登録されている (形式は実ファイルで確認済み)。",
            code: {
              filename: "~/.claude.json",
              language: "json",
              code: `"mcpServers": {
  "playwright": { "command": "npx", "args": ["-y", "@playwright/mcp@0.0.81"] }
}`,
            },
          },
        ],
      },
      {
        client: "codex",
        configPath: "~/.codex/config.toml (mcp_servers)",
        note: "mcp add → config.toml 書き込み → list enabled まで確認済み。",
        steps: [
          {
            id: "pw-register",
            title: "codex mcp add で登録する",
            detail: "この環境で実際に書き込みと一覧表示まで確認した手順。",
            code: {
              filename: "ターミナル",
              language: "bash",
              code: `codex mcp add playwright -- npx -y @playwright/mcp@0.0.81
codex mcp list   # playwright  enabled  と出れば登録成功`,
            },
          },
        ],
      },
      {
        client: "cursor",
        configPath: ".cursor/mcp.json または設定 UI",
        note: "未導入のため未観測。",
        steps: [
          {
            id: "pw-register",
            title: "mcp.json に登録する",
            detail: "command/args 形式の定義が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "mcpServers": {
    "playwright": { "command": "npx", "args": ["-y", "@playwright/mcp@0.0.81"] }
  }
}`,
            },
          },
        ],
      },
      {
        client: "vscode",
        configPath: ".vscode/mcp.json",
        note: "未導入のため未観測。",
        steps: [
          {
            id: "pw-register",
            title: "mcp.json に登録する",
            detail: "servers + stdio 型の定義が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "servers": {
    "playwright": { "type": "stdio", "command": "npx", "args": ["-y", "@playwright/mcp@0.0.81"] }
  }
}`,
            },
          },
        ],
      },
    ],
    connectionCheck: {
      detail:
        "サーバーに initialize → tools/list を投げ、browser_navigate を含む 26 個のツールが返れば接続できている (この環境で確認済み)。クライアントの UI 上ではツール一覧に browser_* が見えるかを見る。",
      code: {
        filename: "ターミナル",
        language: "bash",
        code: `npx -y @playwright/mcp@0.0.81 --help   # 起動自体の確認
# クライアント側では browser_navigate / browser_click 等のツールが見えるかを確認`,
      },
    },
    authScopes: [
      {
        label: "認証方式",
        value: "なし (ローカル stdio)。起動権限 = 設定を書けること。ブラウザは Playwright のキャッシュ (~/Library/Caches/ms-playwright 等) を使う。",
      },
      {
        label: "注意すべきツール",
        value: "tools/list に browser_run_code_unsafe という任意コード実行系のツールが含まれる (実測)。クライアント側でツール許可を絞るかは利用ポリシー次第。",
      },
      {
        label: "ブラウザのアクセス範囲",
        value: "遷移先は任意の URL を取れる。社内サイト・ログイン済みセッションを触らせる範囲はブラウザプロファイルに依存する。",
      },
    ],
    knownFailures: [
      {
        symptom: "ブラウザ未インストール環境で browser_navigate が失敗する",
        observation:
          "未観測。この環境には chromium-1200 が既に入っており、PLAYWRIGHT_BROWSERS_PATH を空ディレクトリに向けても遷移は成功した (既定キャッシュを解決したと思われる)。",
      },
      {
        symptom: "npx 初回起動で handshake が間に合わない",
        observation:
          "Notion MCP で同型を観測 (初回 25 秒タイムアウト → キャッシュ後は即応答)。@playwright/mcp でも初回はパッケージ取得分だけ遅れる可能性がある。",
        workaround: "先に npx -y @playwright/mcp --help を一度実行してキャッシュする。",
      },
    ],
    siblings: ["server", "figma"],
    lastTestedStatus: "起動・一覧・画面遷移まで確認済み",
    lastTestedNote:
      "サーバー応答とブラウザ実操作は検証クライアントで確認、Codex への登録は実コマンドで確認済み。Claude Code/Cursor/VS Code の画面越しの動作は未観測。",
  },
  {
    slug: "notion",
    name: "Notion MCP",
    metaTitle: "Notion MCP の設定方法｜トークン認証とOAuthの違い",
    metaDescription:
      "Notion の MCP サーバー (ホスト型 mcp.notion.com とオープンソース版) を AI ツールに繋ぐ手順。トークン無しでの失敗応答まで実測。",
    keywords: ["Notion MCP", "Notion MCP 設定", "Notion MCP Claude"],
    tagline: "Notion のページ・データベースを操作する MCP サーバー。",
    summary:
      "Notion にはホスト型のリモートサーバー (mcp.notion.com、OAuth) と、自分でプロセスを立てる" +
      "オープンソース版 (@notionhq/notion-mcp-server、インテグレーショントークン) があります。" +
      "後者はトークン無しでも起動・ツール一覧まで進み、API 呼び出し時に 401 で失敗することを確認しました。",
    serverTarget: "@notionhq/notion-mcp-server / https://mcp.notion.com/mcp",
    testedVersion: "2.5.1 / hosted (2026-09-17 時点)",
    lastTestedAt: "2026-09-17",
    testEnvironment: "macOS arm64 / Node 26",
    verifiedObservations: [
      "@notionhq/notion-mcp-server@2.5.1 がトークン無しでも起動し、initialize に応答した (serverInfo {\"name\":\"Notion API\",\"version\":\"1.0.0\"})",
      "tools/list で 24 個の API ツール (API-post-search, API-retrieve-a-page, API-patch-page 等) が返った — トークン不要",
      "NOTION_TOKEN 未設定で API-get-self を呼ぶと 401 unauthorized (Authorization header must use the format \"Bearer <token>\") がツール結果として返った",
      "不正な ntn_ トークンでは 401 \"API token is invalid.\" が返った",
      "ホスト型 https://mcp.notion.com/mcp は未認証で 401 + oauth-protected-resource (scope default、名前 \"Notion MCP (Beta)\") を返した",
    ],
    clients: [...MCP_CLIENT_COLUMNS],
    matrix: [
      {
        feature: "クライアントへの登録 (OSS 版)",
        cells: {
          claude_code: "partial",
          codex: "verified",
          cursor: "unverified",
          vscode: "unverified",
        },
        note: "codex mcp add の登録経路は Playwright MCP で確認済みの同型。Claude Code は設定形式のみ確認。",
      },
      {
        feature: "initialize / tools/list (トークン無し)",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "トークン無しで 24 ツール取得を確認。AI クライアント越しは未観測。",
      },
      {
        feature: "API 呼び出し (実データ)",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "failed",
        },
        note: "トークン無し/不正トークンでは 401 が返ることを確認。有効トークンでの成功は未観測。",
      },
      {
        feature: "ホスト型 (OAuth)",
        cells: {
          claude_code: "unverified",
          codex: "unverified",
          cursor: "unverified",
          vscode: "unverified",
          probe: "verified",
        },
        note: "401 + protected-resource メタデータを確認。OAuth 完了後の操作は未観測。",
      },
    ],
    setups: [
      {
        client: "claude_code",
        configPath: "~/.claude.json (mcpServers + env)",
        note: "設定形式は確認済み。トークンを env に入れる形。",
        steps: [
          {
            id: "notion-token",
            title: "インテグレーショントークンを用意する",
            detail:
              "Notion のインテグレーション設定で内部トークンを発行し、使いたいページ/DB をそのインテグレーションに共有する。トークンは ntn_ または secret_ で始まる。",
          },
          {
            id: "notion-register",
            title: "サーバーを登録する",
            detail: "env に NOTION_TOKEN を渡す形で登録する。",
            code: {
              filename: "~/.claude.json",
              language: "json",
              code: `"mcpServers": {
  "notion": {
    "command": "npx",
    "args": ["-y", "@notionhq/notion-mcp-server@2.5.1"],
    "env": { "NOTION_TOKEN": "ntn_..." }
  }
}`,
            },
          },
        ],
      },
      {
        client: "codex",
        configPath: "~/.codex/config.toml (mcp_servers + env)",
        note: "登録経路は確認済み。env テーブルにトークンを書く形。",
        steps: [
          {
            id: "notion-token",
            title: "インテグレーショントークンを用意する",
            detail: "Notion 側で内部インテグレーションを作り、対象ページを共有する。",
          },
          {
            id: "notion-register",
            title: "codex mcp add で登録する",
            detail:
              "env 付きで登録すると config.toml の mcp_servers に書かれる (list の Env 列の存在は確認済み)。",
            code: {
              filename: "config.toml",
              language: "text",
              code: `[mcp_servers.notion]
command = "npx"
args = ["-y", "@notionhq/notion-mcp-server@2.5.1"]
env = { "NOTION_TOKEN" = "ntn_..." }`,
            },
          },
        ],
      },
      {
        client: "cursor",
        configPath: ".cursor/mcp.json または設定 UI",
        note: "未導入のため未観測。",
        steps: [
          {
            id: "notion-register",
            title: "mcp.json に登録する",
            detail: "command/args/env 形式の定義が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "mcpServers": {
    "notion": {
      "command": "npx",
      "args": ["-y", "@notionhq/notion-mcp-server@2.5.1"],
      "env": { "NOTION_TOKEN": "ntn_..." }
    }
  }
}`,
            },
          },
        ],
      },
      {
        client: "vscode",
        configPath: ".vscode/mcp.json",
        note: "未導入のため未観測。",
        steps: [
          {
            id: "notion-register",
            title: "mcp.json に登録する",
            detail: "servers + stdio 型の定義が案内されている。",
            code: {
              filename: "mcp.json",
              language: "json",
              code: `{
  "servers": {
    "notion": {
      "type": "stdio",
      "command": "npx",
      "args": ["-y", "@notionhq/notion-mcp-server@2.5.1"],
      "env": { "NOTION_TOKEN": "ntn_..." }
    }
  }
}`,
            },
          },
        ],
      },
    ],
    connectionCheck: {
      detail:
        "トークン無しでも tools/list には応答するため、接続確認は API-get-self (自分の情報) を呼んで 401 でなくユーザー情報が返るかで見る。401 が返る場合はトークン未設定または無効 (両方とも実測済み)。",
      code: {
        filename: "ターミナル",
        language: "bash",
        code: `# クライアントから「API-get-self を呼んで」と指示したときの応答で判断
# 401 unauthorized → トークン未設定 / "API token is invalid." → トークン誤り (実測)`,
      },
    },
    authScopes: [
      {
        label: "OSS 版の認証",
        value: "内部インテグレーショントークン (ntn_/secret_ 系) を Bearer ヘッダーで渡す。権限は「そのインテグレーションに共有されたページ/DB」のみ。",
      },
      {
        label: "ホスト型の認証",
        value: "OAuth Bearer。protected-resource メタデータ上の scope は default、リソース名 \"Notion MCP (Beta)\" (確認済み)。",
      },
      {
        label: "失敗の見え方",
        value: "認証失敗はプロトコルエラーではなくツール結果の content に 401 JSON として返る (実測)。クライアント画面上はツール成功に見えることがあるので中身を読む。",
      },
    ],
    knownFailures: [
      {
        symptom: "トークン無しで API 呼び出しが 401 になる",
        observation:
          "API-get-self で \"Authorization header must use the format \\\"Bearer <token>\\\".\" がツール結果として返った (isError フラグは立たない)。",
        workaround: "NOTION_TOKEN を設定して再起動する。",
      },
      {
        symptom: "トークンを入れても 401 が消えない",
        observation:
          "存在しない ntn_ トークンでは \"API token is invalid.\"。有効なトークンでも、ページをインテグレーションに共有していないと検索に出ない (この挙動は未観測)。",
        workaround: "Notion 側で対象ページをインテグレーションに共有する。",
      },
      {
        symptom: "初回の起動が無反応に見える",
        observation:
          "初回 npx 実行でパッケージ取得中は initialize に応答せず、25 秒でタイムアウトした。2 回目以降は即応答 (実測)。",
        workaround: "npx -y @notionhq/notion-mcp-server を一度素で実行してキャッシュする。",
      },
    ],
    siblings: ["figma", "server"],
    lastTestedStatus: "起動・一覧・401 失敗まで確認済み",
    lastTestedNote:
      "起動とツール一覧、トークン無し/不正時の 401 応答は実測済み。実データの読み書きとホスト型 OAuth の完了は有効なワークスペースが必要なため未観測。",
  },
];

export const MCP_GUIDE_BY_SLUG: ReadonlyMap<McpGuideSlug, McpGuide> = new Map(
  MCP_GUIDES.map((guide) => [guide.slug, guide]),
);

/** 兄弟リンク用。自分を除いたガイドを slug 指定で引く */
export function mcpSiblingGuides(slug: McpGuideSlug): readonly McpGuide[] {
  const guide = MCP_GUIDE_BY_SLUG.get(slug);
  if (!guide) return [];
  return guide.siblings
    .map((sibling) => MCP_GUIDE_BY_SLUG.get(sibling))
    .filter((sibling): sibling is McpGuide => sibling !== undefined);
}
